// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - GESTION CULTURES V2 - CRUD COMPLET
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy / Joshua - Janvier 2026
// Créer, Modifier, Réinitialiser, Supprimer, Historique des cultures
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
    Leaf, Plus, Edit, Trash2, RefreshCw, Save, X, Search,
    Calendar, MapPin, Sprout, CheckCircle, AlertTriangle,
    Clock, Archive, Eye, Filter, ChevronDown, ChevronUp,
    Play, Pause, RotateCcw, History
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════════════════
// STATUTS POSSIBLES
// ═══════════════════════════════════════════════════════════════════════════
const STATUTS = [
    { code: 'planifie', label: 'Planifié', couleur: 'bg-gray-100 text-gray-700', icone: '📅' },
    { code: 'seme', label: 'Semé', couleur: 'bg-blue-100 text-blue-700', icone: '🌱' },
    { code: 'repique', label: 'Repiqué', couleur: 'bg-green-100 text-green-700', icone: '🌿' },
    { code: 'croissance', label: 'Croissance', couleur: 'bg-emerald-100 text-emerald-700', icone: '🌳' },
    { code: 'floraison', label: 'Floraison', couleur: 'bg-pink-100 text-pink-700', icone: '🌸' },
    { code: 'recolte', label: 'En récolte', couleur: 'bg-orange-100 text-orange-700', icone: '🌾' },
    { code: 'termine', label: 'Terminé', couleur: 'bg-slate-100 text-slate-700', icone: '✅' },
    { code: 'echec', label: 'Échec', couleur: 'bg-red-100 text-red-700', icone: '❌' },
    { code: 'abandonne', label: 'Abandonné', couleur: 'bg-red-50 text-red-600', icone: '🚫' }
];

const GestionCulturesV2 = () => {
    // ─── États ────────────────────────────────────────────────────────────
    const [cultures, setCultures] = useState([]);
    const [parcelles, setParcelles] = useState([]);
    const [varietes, setVarietes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [filtreStatut, setFiltreStatut] = useState('actives'); // actives, toutes, terminees
    const [recherche, setRecherche] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create, edit, view
    const [selectedCulture, setSelectedCulture] = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());

    // ─── Formulaire ───────────────────────────────────────────────────────
    const [formData, setFormData] = useState({
        parcelle_id: '',
        variete_id: '',
        surface_m2: '',
        date_prevue_semis: '',
        date_prevue_repiquage: '',
        date_prevue_recolte: '',
        date_reelle_semis: '',
        date_reelle_repiquage: '',
        date_reelle_recolte: '',
        statut: 'planifie',
        notes: ''
    });

    // ─── Chargement ───────────────────────────────────────────────────────
    useEffect(() => {
        loadData();
    }, [annee]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Cultures
            const culturesRes = await fetch(`${API_BASE}/cultures?annee=${annee}`);
            const culturesData = await culturesRes.json();
            if (culturesData.success) {
                setCultures(culturesData.data || []);
            }

            // Parcelles
            const parcellesRes = await fetch(`${API_BASE}/parcelles-gestion/liste-complete`);
            const parcellesData = await parcellesRes.json();
            if (parcellesData.success) {
                setParcelles(parcellesData.data || []);
            }

            // Variétés
            const varietesRes = await fetch(`${API_BASE}/varietes`);
            const varietesData = await varietesRes.json();
            if (varietesData.success) {
                setVarietes(varietesData.data || []);
            }
        } catch (err) {
            console.error('Erreur chargement:', err);
        } finally {
            setLoading(false);
        }
    };

    // ─── Filtrage ─────────────────────────────────────────────────────────
    const culturesFiltrees = useMemo(() => {
        let filtered = [...cultures];

        // Filtre par statut
        if (filtreStatut === 'actives') {
            filtered = filtered.filter(c => !['termine', 'echec', 'abandonne'].includes(c.statut));
        } else if (filtreStatut === 'terminees') {
            filtered = filtered.filter(c => ['termine', 'echec', 'abandonne'].includes(c.statut));
        }

        // Recherche
        if (recherche) {
            const search = recherche.toLowerCase();
            filtered = filtered.filter(c =>
                c.code_culture?.toLowerCase().includes(search) ||
                c.variete_nom?.toLowerCase().includes(search) ||
                c.parcelle_nom?.toLowerCase().includes(search)
            );
        }

        return filtered;
    }, [cultures, filtreStatut, recherche]);

    // ─── Actions CRUD ─────────────────────────────────────────────────────
    const openCreateModal = () => {
        setFormData({
            parcelle_id: '',
            variete_id: '',
            surface_m2: '',
            date_prevue_semis: '',
            date_prevue_repiquage: '',
            date_prevue_recolte: '',
            date_reelle_semis: '',
            date_reelle_repiquage: '',
            date_reelle_recolte: '',
            statut: 'planifie',
            notes: ''
        });
        setModalMode('create');
        setSelectedCulture(null);
        setShowModal(true);
    };

    const openEditModal = (culture) => {
        setFormData({
            parcelle_id: culture.parcelle_id || '',
            variete_id: culture.variete_id || '',
            surface_m2: culture.surface_m2 || '',
            date_prevue_semis: culture.date_prevue_semis?.split('T')[0] || '',
            date_prevue_repiquage: culture.date_prevue_repiquage?.split('T')[0] || '',
            date_prevue_recolte: culture.date_prevue_recolte?.split('T')[0] || '',
            date_reelle_semis: culture.date_reelle_semis?.split('T')[0] || '',
            date_reelle_repiquage: culture.date_reelle_repiquage?.split('T')[0] || '',
            date_reelle_recolte: culture.date_reelle_recolte?.split('T')[0] || '',
            statut: culture.statut || 'planifie',
            notes: culture.notes || ''
        });
        setModalMode('edit');
        setSelectedCulture(culture);
        setShowModal(true);
    };

    const openViewModal = (culture) => {
        setSelectedCulture(culture);
        setModalMode('view');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const url = modalMode === 'create'
                ? `${API_BASE}/cultures`
                : `${API_BASE}/cultures/${selectedCulture.id}`;

            const method = modalMode === 'create' ? 'POST' : 'PUT';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    annee
                })
            });

            const data = await res.json();

            if (data.success) {
                alert(modalMode === 'create' ? 'Culture créée !' : 'Culture mise à jour !');
                setShowModal(false);
                loadData();
            } else {
                alert('Erreur: ' + data.message);
            }
        } catch (err) {
            console.error('Erreur:', err);
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleDelete = async (culture) => {
        if (!confirm(`Supprimer la culture ${culture.code_culture} ?`)) return;

        try {
            const res = await fetch(`${API_BASE}/cultures/${culture.id}`, {
                method: 'DELETE'
            });
            const data = await res.json();

            if (data.success) {
                alert('Culture supprimée');
                loadData();
            } else {
                alert('Erreur: ' + data.message);
            }
        } catch (err) {
            console.error('Erreur:', err);
            alert('Erreur lors de la suppression');
        }
    };

    const handleReset = async (culture) => {
        if (!confirm(`Réinitialiser la culture ${culture.code_culture} ? Les données seront remises à zéro.`)) return;

        try {
            const res = await fetch(`${API_BASE}/cultures/${culture.id}/reset`, {
                method: 'POST'
            });
            const data = await res.json();

            if (data.success) {
                alert('Culture réinitialisée');
                loadData();
            } else {
                alert('Erreur: ' + data.message);
            }
        } catch (err) {
            console.error('Erreur:', err);
            alert('Erreur lors de la réinitialisation');
        }
    };

    const handleChangeStatut = async (culture, nouveauStatut) => {
        try {
            const res = await fetch(`${API_BASE}/cultures/${culture.id}/statut`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ statut: nouveauStatut })
            });
            const data = await res.json();

            if (data.success) {
                loadData();
            } else {
                alert('Erreur: ' + data.message);
            }
        } catch (err) {
            console.error('Erreur:', err);
        }
    };

    // ─── Toggle détails ───────────────────────────────────────────────────
    const toggleRow = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    // ─── Calcul jours ─────────────────────────────────────────────────────
    const getJoursDepuisPlantation = (culture) => {
        const dateRef = culture.date_reelle_semis || culture.date_reelle_repiquage;
        if (!dateRef) return null;
        const jours = Math.floor((new Date() - new Date(dateRef)) / (1000 * 60 * 60 * 24));
        return jours;
    };

    // ─── Stats ────────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const actives = cultures.filter(c => !['termine', 'echec', 'abandonne'].includes(c.statut));
        return {
            total: cultures.length,
            actives: actives.length,
            terminees: cultures.filter(c => c.statut === 'termine').length,
            echecs: cultures.filter(c => ['echec', 'abandonne'].includes(c.statut)).length,
            surface: actives.reduce((sum, c) => sum + parseFloat(c.surface_m2 || 0), 0)
        };
    }, [cultures]);

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-2 text-gray-600">Chargement...</span>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Leaf className="w-7 h-7 text-green-600" />
                    🌱 Gestion des Cultures
                </h1>

                <div className="flex items-center gap-3">
                    {/* Sélecteur année */}
                    <select
                        value={annee}
                        onChange={(e) => setAnnee(parseInt(e.target.value))}
                        className="border rounded-lg px-3 py-2"
                    >
                        {[2024, 2025, 2026, 2027].map(a => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>

                    <button
                        onClick={loadData}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>

                    <button
                        onClick={openCreateModal}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                        <Plus className="w-4 h-4" /> Nouvelle Culture
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-gray-700">{stats.total}</div>
                    <div className="text-sm text-gray-500">Total {annee}</div>
                </div>
                <div className="bg-green-50 rounded-xl shadow p-4 text-center border-2 border-green-200">
                    <div className="text-3xl font-bold text-green-600">{stats.actives}</div>
                    <div className="text-sm text-green-600">Actives</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-slate-600">{stats.terminees}</div>
                    <div className="text-sm text-gray-500">Terminées</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-red-500">{stats.echecs}</div>
                    <div className="text-sm text-gray-500">Échecs</div>
                </div>
                <div className="bg-blue-50 rounded-xl shadow p-4 text-center border-2 border-blue-200">
                    <div className="text-3xl font-bold text-blue-600">{stats.surface.toFixed(0)}</div>
                    <div className="text-sm text-blue-600">m² cultivés</div>
                </div>
            </div>

            {/* Filtres */}
            <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 bg-white rounded-lg shadow px-3 py-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                        value={filtreStatut}
                        onChange={(e) => setFiltreStatut(e.target.value)}
                        className="border-none focus:ring-0 text-sm"
                    >
                        <option value="actives">Cultures actives</option>
                        <option value="toutes">Toutes</option>
                        <option value="terminees">Terminées/Échecs</option>
                    </select>
                </div>

                <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher par code, variété, parcelle..."
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                </div>
            </div>

            {/* Tableau des cultures */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Culture</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Parcelle</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Surface</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Statut</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Progression</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {culturesFiltrees.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                    <Sprout className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                    Aucune culture trouvée
                                </td>
                            </tr>
                        ) : (
                            culturesFiltrees.map(culture => {
                                const statut = STATUTS.find(s => s.code === culture.statut) || STATUTS[0];
                                const jours = getJoursDepuisPlantation(culture);
                                const isExpanded = expandedRows.has(culture.id);

                                return (
                                    <React.Fragment key={culture.id}>
                                        <tr className="hover:bg-gray-50">
                                            {/* Culture */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => toggleRow(culture.id)}
                                                        className="text-gray-400 hover:text-gray-600"
                                                    >
                                                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                    </button>
                                                    <span className="text-2xl">{culture.variete_icone || '🌱'}</span>
                                                    <div>
                                                        <div className="font-medium text-gray-800">
                                                            {culture.variete_nom || culture.code_culture}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{culture.code_culture}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Parcelle */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-gray-400" />
                                                    <span>{culture.parcelle_nom || '-'}</span>
                                                </div>
                                            </td>

                                            {/* Surface */}
                                            <td className="px-4 py-3">
                                                <span className="font-mono">{culture.surface_m2 || '-'} m²</span>
                                            </td>

                                            {/* Statut */}
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${statut.couleur}`}>
                                                    {statut.icone} {statut.label}
                                                </span>
                                            </td>

                                            {/* Progression */}
                                            <td className="px-4 py-3">
                                                {jours !== null ? (
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-gray-400" />
                                                        <span className="font-medium">J+{jours}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openViewModal(culture)}
                                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                                                        title="Voir détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => openEditModal(culture)}
                                                        className="p-2 text-blue-500 hover:bg-blue-50 rounded"
                                                        title="Modifier"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleReset(culture)}
                                                        className="p-2 text-orange-500 hover:bg-orange-50 rounded"
                                                        title="Réinitialiser"
                                                    >
                                                        <RotateCcw className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(culture)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Ligne détails expandable */}
                                        {isExpanded && (
                                            <tr className="bg-gray-50">
                                                <td colSpan="6" className="px-4 py-4">
                                                    <div className="grid grid-cols-4 gap-4">
                                                        <div>
                                                            <h4 className="font-medium text-gray-700 mb-2">📅 Dates prévues</h4>
                                                            <div className="text-sm space-y-1">
                                                                <div>Semis: {culture.date_prevue_semis?.split('T')[0] || '-'}</div>
                                                                <div>Repiquage: {culture.date_prevue_repiquage?.split('T')[0] || '-'}</div>
                                                                <div>Récolte: {culture.date_prevue_recolte?.split('T')[0] || '-'}</div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-medium text-gray-700 mb-2">✅ Dates réelles</h4>
                                                            <div className="text-sm space-y-1">
                                                                <div>Semis: {culture.date_reelle_semis?.split('T')[0] || '-'}</div>
                                                                <div>Repiquage: {culture.date_reelle_repiquage?.split('T')[0] || '-'}</div>
                                                                <div>Récolte: {culture.date_reelle_recolte?.split('T')[0] || '-'}</div>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-medium text-gray-700 mb-2">🔄 Changer statut</h4>
                                                            <div className="flex flex-wrap gap-1">
                                                                {STATUTS.slice(0, 6).map(s => (
                                                                    <button
                                                                        key={s.code}
                                                                        onClick={() => handleChangeStatut(culture, s.code)}
                                                                        className={`px-2 py-1 text-xs rounded ${
                                                                            culture.statut === s.code
                                                                                ? 'ring-2 ring-green-500 ' + s.couleur
                                                                                : s.couleur + ' opacity-60 hover:opacity-100'
                                                                        }`}
                                                                    >
                                                                        {s.icone}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-medium text-gray-700 mb-2">📝 Notes</h4>
                                                            <div className="text-sm text-gray-600">
                                                                {culture.notes || 'Aucune note'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal Création/Édition */}
            {showModal && modalMode !== 'view' && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex items-center justify-between">
                            <h2 className="text-xl font-bold">
                                {modalMode === 'create' ? '➕ Nouvelle Culture' : '✏️ Modifier Culture'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                {/* Parcelle */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📍 Parcelle *
                                    </label>
                                    <select
                                        value={formData.parcelle_id}
                                        onChange={(e) => setFormData({ ...formData, parcelle_id: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                        required
                                    >
                                        <option value="">Sélectionner...</option>
                                        {parcelles.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.tranche_nom} &gt; {p.nom} ({p.surface_m2}m²)
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Variété */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        🌱 Variété *
                                    </label>
                                    <select
                                        value={formData.variete_id}
                                        onChange={(e) => setFormData({ ...formData, variete_id: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                        required
                                    >
                                        <option value="">Sélectionner...</option>
                                        {varietes.map(v => (
                                            <option key={v.id} value={v.id}>
                                                {v.icone} {v.espece} - {v.variete}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Surface */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📐 Surface (m²)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.surface_m2}
                                        onChange={(e) => setFormData({ ...formData, surface_m2: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>

                                {/* Statut */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📊 Statut
                                    </label>
                                    <select
                                        value={formData.statut}
                                        onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    >
                                        {STATUTS.map(s => (
                                            <option key={s.code} value={s.code}>
                                                {s.icone} {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📅 Semis prévu
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_prevue_semis}
                                        onChange={(e) => setFormData({ ...formData, date_prevue_semis: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📅 Repiquage prévu
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_prevue_repiquage}
                                        onChange={(e) => setFormData({ ...formData, date_prevue_repiquage: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        📅 Récolte prévue
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_prevue_recolte}
                                        onChange={(e) => setFormData({ ...formData, date_prevue_recolte: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ✅ Semis réel
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_reelle_semis}
                                        onChange={(e) => setFormData({ ...formData, date_reelle_semis: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ✅ Repiquage réel
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_reelle_repiquage}
                                        onChange={(e) => setFormData({ ...formData, date_reelle_repiquage: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ✅ Récolte réelle
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.date_reelle_recolte}
                                        onChange={(e) => setFormData({ ...formData, date_reelle_recolte: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    📝 Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    rows="3"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                                >
                                    <Save className="w-4 h-4" />
                                    {modalMode === 'create' ? 'Créer' : 'Sauvegarder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionCulturesV2;